/**
 * "Someone else just moved this" (#1465 step 4). Built to
 * `threads/live-grid-push/mockup-conflict-guard.html` — Corbin, 2026-09-11:
 * keep "Move it anyway", name people, keep the glow.
 *
 * Shown when a drop comes back 409 STALE_JOB: the job's placement changed
 * after this screen last read it. The dialog says who, when, and where the
 * job is now; the board has already been refreshed underneath.
 *
 * Words are derived by `describeStaleJob` (pure, tested). Three shapes:
 *   moved   — "Dana Whitfield moved … It is now on Tomas Alvarez at 1:00 PM."
 *   auto    — "The auto-scheduler placed …"
 *   status  — "Marcus Reed marked … In progress from the field …" (no move-
 *             anyway when the job has started: the status rules forbid it)
 * Unknown writers read as "Someone".
 */
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";

export interface StaleJobBody {
    error: "STALE_JOB";
    message?: string;
    changedAt: string;
    changedBy: { id: string; name: string; role: string | null } | null;
    changedByKind: "user" | "auto" | "unknown";
    now: {
        title: string | null;
        customerName: string | null;
        status: string | null;
        assignedTo: string | null;
        assignedToName: string | null;
        scheduledStart: string | null;
        scheduledEnd: string | null;
    };
}

/** What the dispatcher was trying to do, as the board knew the job. */
export interface StaleJobAttempt {
    /** The job as this screen had it before the drop. */
    before: { assigned_to: string | null; scheduled_start: string | null; status: string };
    /** Where they dropped it. */
    dropTechName: string | null;
    dropStart: string | null;
}

export interface StaleJobWords {
    title: string;
    /** First paragraph: who did what, where it is now. */
    what: string;
    /** Second paragraph: what happened to your drop, and the board's state. */
    yours: string;
    canMoveAnyway: boolean;
}

const STARTED = new Set(["en_route", "arrived", "in_progress"]);
const STATUS_WORDS: Record<string, string> = {
    draft: "Draft",
    awaiting_scheduling: "Awaiting dispatch",
    unqueued: "Unqueued",
    scheduled: "Scheduled",
    en_route: "En route",
    arrived: "Arrived",
    in_progress: "In progress",
    office_review: "Office review",
    cannot_complete: "Cannot complete",
    held: "Held",
    completed: "Completed",
    closed: "Closed",
    cancelled: "Cancelled",
};

export function agoWords(iso: string, now: number): string {
    const ms = Math.max(0, now - Date.parse(iso));
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s} second${s === 1 ? "" : "s"} ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
    const h = Math.round(m / 60);
    return `${h} hour${h === 1 ? "" : "s"} ago`;
}

function clock(iso: string): string {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
function sameDay(a: string | null, b: string | null): boolean {
    if (!a || !b) return true;
    return new Date(a).toDateString() === new Date(b).toDateString();
}
/** "1:00 PM", or "Fri, Sep 12 at 1:00 PM" when it left the day you are on. */
function whenWords(iso: string, relativeTo: string | null): string {
    if (sameDay(iso, relativeTo)) return clock(iso);
    const day = new Date(iso).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
    return `${day} at ${clock(iso)}`;
}

export function describeStaleJob(body: StaleJobBody, attempt: StaleJobAttempt, now: number): StaleJobWords {
    const jobLabel = [body.now.customerName, body.now.title].filter(Boolean).join(" · ") || "this job";
    const ago = agoWords(body.changedAt, now);
    const who = body.changedByKind === "auto" ? "The auto-scheduler" : (body.changedBy?.name ?? "Someone");
    const fromField = body.changedBy?.role === "Technician" ? " from the field" : "";
    const moved =
        body.now.assignedTo !== attempt.before.assigned_to ||
        body.now.scheduledStart !== attempt.before.scheduled_start;
    const statusChanged = (body.now.status ?? "") !== attempt.before.status;
    const started = STARTED.has(body.now.status ?? "");

    const nowWhere = body.now.assignedTo
        ? `It is now on ${body.now.assignedToName ?? "another tech"}${body.now.scheduledStart ? ` at ${whenWords(body.now.scheduledStart, attempt.dropStart)}` : ""}.`
        : "It is now back in Awaiting Dispatch.";
    const yourDrop = attempt.dropTechName
        ? `Your drop onto ${attempt.dropTechName}${attempt.dropStart ? ` at ${clock(attempt.dropStart)}` : ""} was not applied.`
        : "Your move was not applied.";

    if (moved || !statusChanged) {
        const verb = body.changedByKind === "auto" ? "placed" : "moved";
        return {
            title:
                body.changedByKind === "auto" ? "The auto-scheduler just moved this" : "Someone else just moved this",
            what: `${who} ${verb} ${jobLabel} ${ago}. ${nowWhere}`,
            yours: `${yourDrop} The board has been refreshed, so you are looking at the current day.`,
            canMoveAnyway: !started,
        };
    }
    // Same place, different status.
    const statusWord = STATUS_WORDS[body.now.status ?? ""] ?? body.now.status ?? "changed";
    return {
        title: "This job just changed",
        what: `${who} marked ${jobLabel} ${statusWord}${fromField} ${ago}.`,
        yours: started
            ? `${yourDrop} A job that has started stays with the tech who started it. The board has been refreshed.`
            : `${yourDrop} The board has been refreshed, so you are looking at the current day.`,
        canMoveAnyway: !started,
    };
}

export interface StaleJobDialogProps {
    open: boolean;
    body: StaleJobBody;
    attempt: StaleJobAttempt;
    onShowMe: () => void;
    onMoveAnyway: () => void;
}

export function StaleJobDialog({ open, body, attempt, onShowMe, onMoveAnyway }: StaleJobDialogProps) {
    const w = describeStaleJob(body, attempt, Date.now());
    return (
        <Dialog open={open} title={w.title} onClose={onShowMe} className="max-w-sm">
            <div className="space-y-4" data-testid="stale-job-dialog">
                <p className="text-sm whitespace-pre-line">
                    {w.what}
                    {"\n\n"}
                    {w.yours}
                </p>
                <div className="flex justify-end gap-2">
                    {w.canMoveAnyway && (
                        <Button variant="outline" onClick={onMoveAnyway}>
                            Move it anyway
                        </Button>
                    )}
                    <Button autoFocus onClick={onShowMe}>
                        OK, show me
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
